import { useCallback, useEffect, useState } from 'react';
import { Send } from 'lucide-react';
import { getCrmClientMessages, sendCrmClientMessage, type CrmMessage } from '../../../api';

type Props = {
  userId: number;
  orderId?: number;
  hapticSelection: () => void;
  onCreateLead?: () => void | Promise<void>;
};

export function CrmChatThread({ userId, orderId, hapticSelection, onCreateLead }: Props) {
  const [messages, setMessages] = useState<CrmMessage[]>([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCrmClientMessages(userId, { limit: 80 });
      setMessages(data);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const send = async () => {
    const body = text.trim();
    if (!body) return;
    hapticSelection();
    setSending(true);
    try {
      const msg = await sendCrmClientMessage(userId, body, orderId);
      setMessages((prev) => [...prev, msg]);
      setText('');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {onCreateLead && (
        <button
          type="button"
          onClick={() => void onCreateLead()}
          className="press w-full py-2.5 rounded-xl text-xs font-bold touch-manipulation min-h-[44px]"
          style={{ background: 'rgba(246,173,85,0.12)', color: '#F6AD55', border: '1px solid rgba(246,173,85,0.35)' }}
        >
          Создать лид из переписки
        </button>
      )}
      <div
        className="max-h-56 overflow-y-auto space-y-2 p-2 rounded-xl"
        style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid var(--border-subtle)' }}
      >
        {loading && (
          <p className="text-xs text-center py-4" style={{ color: 'var(--text-3)' }}>
            Загрузка…
          </p>
        )}
        {!loading && messages.length === 0 && (
          <p className="text-xs text-center py-4" style={{ color: 'var(--text-3)' }}>
            Сообщений пока нет. Клиент пишет в Telegram — они появятся здесь.
          </p>
        )}
        {messages.map((m) => (
          <div
            key={m.id}
            className={`max-w-[90%] px-3 py-2 rounded-xl text-xs ${
              m.direction === 'outbound' ? 'ml-auto text-right' : 'mr-auto text-left'
            }`}
            style={
              m.direction === 'outbound'
                ? { background: 'rgba(99,179,237,0.25)', color: 'var(--text-1)' }
                : { background: 'rgba(255,255,255,0.08)', color: 'var(--text-2)' }
            }
          >
            <p className="whitespace-pre-wrap break-words">{m.body || '—'}</p>
            {m.created_at && (
              <p className="text-[9px] mt-1 opacity-60">
                {new Date(m.created_at).toLocaleString('ru-RU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </p>
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          className="input-glass flex-1 text-sm"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Сообщение в Telegram…"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
        />
        <button
          type="button"
          disabled={sending || !text.trim()}
          onClick={() => void send()}
          className="press px-3 rounded-xl"
          style={{ background: 'rgba(99,179,237,0.2)', border: '1px solid rgba(99,179,237,0.35)' }}
        >
          <Send size={18} color="#63B3ED" />
        </button>
      </div>
    </div>
  );
}
